import React from 'react';
import { Link } from 'react-router-dom';
import { Award, TrendingUp, FileText } from 'lucide-react';
import { useResumeStrength } from '../../hooks/resume/useResumeStrength';
import { LoadingSpinner } from '../common/LoadingSpinner';

interface ResumeStrengthCardProps {
  userId: string;
}

export function ResumeStrengthCard({ userId }: ResumeStrengthCardProps) {
  const { metrics, isLoading } = useResumeStrength(userId);

  if (isLoading) {
    return (
      <div className="bg-[#9d9a98] rounded-xl p-6 shadow-lg">
        <LoadingSpinner />
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="bg-[#9d9a98] rounded-xl p-6 shadow-lg">
        <div className="text-center space-y-4">
          <FileText className="h-8 w-8 text-black mx-auto mb-2" />
          <p className="text-black">No resume scores yet. Upload your resume to get started.</p>
          <Link
            to="/resume-review"
            className="inline-block px-4 py-2 bg-black text-white rounded-lg hover:opacity-90 transition-colors text-sm"
          >
            Upload Resume
          </Link>
        </div>
      </div>
    );
  }

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 70) return 'Highly Competitive';
    if (score >= 40) return 'Needs Optimization';
    return 'Needs Major Improvement';
  };

  return (
    <div className="bg-[#9d9a98] rounded-xl p-6 shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Award className="h-6 w-6 text-[#800020] mr-2" />
          <h2 className="text-xl font-semibold text-black">Resume Strength</h2>
        </div>
        <div className="flex items-center">
          <TrendingUp className="h-5 w-5 text-green-500 mr-2" />
          <span className="text-sm text-black">{metrics.iterations} iterations</span>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-black">Overall Score</span>
          <span className="text-2xl font-bold text-black">
            {metrics.currentScore}%
          </span>
        </div>
        <div className="bg-gray-200 rounded-full h-2 mb-2">
          <div 
            className="bg-black h-2 rounded-full transition-all duration-300"
            style={{ width: `${metrics.currentScore}%` }}
          />
        </div>
        <p className="text-sm text-black">
          {getScoreLabel(metrics.currentScore)}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <h3 className="text-sm font-medium text-black mb-2">Keywords</h3>
          <div className="bg-gray-200 rounded-full h-2 mb-1">
            <div 
              className="bg-black h-2 rounded-full"
              style={{ width: `${metrics.criteriaScores.keywords}%` }}
            />
          </div>
          <span className="text-sm text-black">{metrics.criteriaScores.keywords}%</span>
        </div>

        <div>
          <h3 className="text-sm font-medium text-black mb-2">Formatting</h3>
          <div className="bg-gray-200 rounded-full h-2 mb-1">
            <div 
              className="bg-black h-2 rounded-full"
              style={{ width: `${metrics.criteriaScores.formatting}%` }}
            />
          </div>
          <span className="text-sm text-black">{metrics.criteriaScores.formatting}%</span>
        </div>

        <div>
          <h3 className="text-sm font-medium text-black mb-2">Content</h3>
          <div className="bg-gray-200 rounded-full h-2 mb-1">
            <div 
              className="bg-black h-2 rounded-full"
              style={{ width: `${metrics.criteriaScores.content}%` }}
            />
          </div>
          <span className="text-sm text-black">{metrics.criteriaScores.content}%</span>
        </div>

        <div>
          <h3 className="text-sm font-medium text-black mb-2">ATS</h3>
          <div className="bg-gray-200 rounded-full h-2 mb-1">
            <div 
              className="bg-black h-2 rounded-full"
              style={{ width: `${metrics.criteriaScores.ats}%` }}
            />
          </div>
          <span className="text-sm text-black">{metrics.criteriaScores.ats}%</span>
        </div>
      </div>

      {metrics.latestFeedback && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-black mb-2">Latest Feedback</h3>
          <p className="text-sm text-black">{metrics.latestFeedback}</p>
        </div>
      )}
    </div>
  );
}